import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";
import { toast } from "react-toastify";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

import { forgotPassword } from "~/api/v2/requests";
import { Button } from "~/atoms/button";
import { AuthLayout } from "../../layouts/Auth";
import { Input } from "~/atoms/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/atoms/form";
import i18n from "~/i18n";

const FormSchema = z.object({
  email: z.email({ message: i18n.t(($) => $.validations.email) }),
});

type ForgotPasswordValues = z.infer<typeof FormSchema>;

export const ForgotPassword = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const form = useForm({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      email: "",
    },
  });

  const onSubmit = async (values: ForgotPasswordValues) => {
    try {
      setIsLoading(true);
      await forgotPassword({ email: values.email });
      await navigate("/email-sent");
    } catch {
      toast.error(t(($) => $.toasts.error.email));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout
      title={t(($) => $.password.forgot.title)}
      description={t(($) => $.password.forgot.description)}
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="mt-4">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t(($) => $.password.email.title)}</FormLabel>
                <FormControl>
                  <Input
                    placeholder={t(($) => $.password.email.enter)}
                    autoComplete="off"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex justify-between pt-5 font-medium">
            <Button variant="link" asChild>
              <Link to="/login">{t(($) => $.password.backToLogin)}</Link>
            </Button>
            <Button type="submit" disabled={isLoading}>
              {t(($) => $.commons.buttons.resetPass)}
            </Button>
          </div>
        </form>
      </Form>
    </AuthLayout>
  );
};
